import { Injectable, signal } from "@angular/core";

// Timeout durations in milliseconds
const WARNING_TIMEOUT_MS = 25 * 60 * 1000; // 25 minutes
const SESSION_END_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

@Injectable({
  providedIn: "root",
})
export class ScanSessionTimeoutService {
  private warningTimerId: ReturnType<typeof setTimeout> | null = null;
  private sessionEndTimerId: ReturnType<typeof setTimeout> | null = null;

  // Signal to show/hide the warning dialog
  private _showWarningDialog = signal(false);
  public readonly showWarningDialog = this._showWarningDialog.asReadonly();

  // Signal to track if session should end due to timeout
  private _sessionTimedOut = signal(false);
  public readonly sessionTimedOut = this._sessionTimedOut.asReadonly();

  // Track if timers are active
  private _isTracking = signal(false);
  public readonly isTracking = this._isTracking.asReadonly();

  /**
   * Start tracking the session timeout
   * Call this when a scanning session begins
   */
  public startTracking(): void {
    this.clearAllTimers();
    this._isTracking.set(true);
    this._sessionTimedOut.set(false);
    this._showWarningDialog.set(false);

    // Set warning timer (25 minutes)
    this.warningTimerId = setTimeout(() => {
      this.onWarningTimeout();
    }, WARNING_TIMEOUT_MS);

    console.log(
      "[SessionTimeout] Started tracking - warning in 25min, end in 30min",
    );
  }

  /**
   * Reset the timers (e.g., when user confirms they're still scanning)
   */
  public resetTimers(): void {
    console.log("[SessionTimeout] Resetting timers");
    this._showWarningDialog.set(false);
    this.startTracking();
  }

  /**
   * Stop tracking (e.g., when session ends normally)
   */
  public stopTracking(): void {
    console.log("[SessionTimeout] Stopped tracking");
    this.clearAllTimers();
    this._isTracking.set(false);
    this._showWarningDialog.set(false);
    this._sessionTimedOut.set(false);
  }

  /**
   * User responded "Yes" - they're still scanning
   * Reset all timers and close dialog
   */
  public userConfirmedStillScanning(): void {
    console.log("[SessionTimeout] User confirmed still scanning");
    this.resetTimers();
  }

  /**
   * User responded "No" - they want to end the session
   */
  public userRequestedEndSession(): void {
    console.log("[SessionTimeout] User requested end session");
    this._showWarningDialog.set(false);
    this._sessionTimedOut.set(true);
    this.clearAllTimers();
  }

  /**
   * Called when session ends (for any reason)
   * Resets the timers for a potential new session
   */
  public onSessionEnded(): void {
    console.log("[SessionTimeout] Session ended - resetting for next session");
    this.stopTracking();
  }

  /**
   * Close the warning dialog without taking action
   * (e.g., if session ends through other means while dialog is open)
   */
  public dismissWarningDialog(): void {
    this._showWarningDialog.set(false);
  }

  private onWarningTimeout(): void {
    console.log("[SessionTimeout] Warning timeout reached - showing dialog");
    this._showWarningDialog.set(true);

    // Set the final session end timer (5 more minutes)
    const remainingTime = SESSION_END_TIMEOUT_MS - WARNING_TIMEOUT_MS;
    this.sessionEndTimerId = setTimeout(() => {
      this.onSessionEndTimeout();
    }, remainingTime);
  }

  private onSessionEndTimeout(): void {
    console.log(
      "[SessionTimeout] Session end timeout reached - auto-ending session",
    );
    this._showWarningDialog.set(false);
    this._sessionTimedOut.set(true);
  }

  private clearAllTimers(): void {
    if (this.warningTimerId) {
      clearTimeout(this.warningTimerId);
      this.warningTimerId = null;
    }
    if (this.sessionEndTimerId) {
      clearTimeout(this.sessionEndTimerId);
      this.sessionEndTimerId = null;
    }
  }
}
